import {getEditorValidationErrorFromPath} from '../editors/commons/editorErrors';
import {Expression} from '../editors/expressions/expressionModel';
import {getExpressionRefsValidationErrors} from '../editors/expressions/expressionValidationHelpers';
import {fromDefinitionItem, getParamsErrors, typeAndParamsEqual} from './ruleActionConditionHelpers';
import {ClientRuleAction, RuleAction} from './ruleActionModel';
import {RuleEditorActionErrorsEnum} from './ruleEditor/ruleEditorErrors';
import {RuleActionDefinition} from './ruleParameterModel';

// Build a generic empty / default action from a definition spec.
export function fromDefinitionAction(actionDefinition: RuleActionDefinition, clientId?: string) {
  return fromDefinitionItem(actionDefinition, clientId);
}

export function checkClientRuleActionsEquality(a: ClientRuleAction, b: ClientRuleAction) {
  return typeAndParamsEqual(a, b);
}

export function getActionErrors(
  action: RuleAction,
  actionDefinition: RuleActionDefinition | undefined,
  expressions: ReadonlyArray<Expression>,
): ReadonlyArray<RuleEditorActionErrorsEnum> {
  if (!('params' in action)) {
    return [];
  }

  // Fallback on approximate validation.
  if (!actionDefinition) {
    console.warn('No definition found for action', action);
    return getParamsErrors(action);
  }

  const errors = actionDefinition.validate(action.params);
  if (errors.length > 0) {
    return errors.map((error) => getEditorValidationErrorFromPath(error.path));
  }

  return getExpressionRefsValidationErrors(action.params, expressions);
}

export function checkActionValidity(
  action: ClientRuleAction,
  actionDefinition: RuleActionDefinition | undefined,
  expressions: ReadonlyArray<Expression>,
): boolean {
  return getActionErrors(action, actionDefinition, expressions).length === 0;
}

export function createActionWithParamsAndClientId<TAction extends RuleAction>(
  actionDefinition: RuleActionDefinition,
  params: TAction['params'],
  stableClientId: string,
): ClientRuleAction {
  const action = fromDefinitionAction(actionDefinition, stableClientId);

  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return {
    ...action,
    params,
  } as ClientRuleAction;
}

type ActionFromType<TAction extends RuleAction, TActionType extends RuleAction['type']> = TAction extends {
  type: TActionType;
}
  ? TAction
  : never;

export function buildRuleAction<
  TActionType extends RuleAction['type'],
  TAction extends ActionFromType<RuleAction, TActionType>,
>(type: TActionType, params: TAction['params']): TAction {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return {
    type,
    integration_id: 0,
    params,
  } as TAction;
}
